"use client";

import { formatCompactUSD } from "../helpers/format-money";

interface MarketsListVolumeCellsProps {
  /** Pulled directly from the event row. Volume = lifetime / 24hr depending on source. */
  volume: number;
  volume24hr: number;
  liquidity: number;
  effectiveSourceFilter: string;
}

/**
 * Three sibling `<td>` cells for the markets list-view row:
 *   1. 24h Volume (Kalshi shows lifetime volume here since they don't ship a 24hr field)
 *   2. Liquidity — PM only. Non-PM sources skip the column entirely (no data).
 *   3. Total Volume (lifetime)
 *
 * Returns a Fragment so it slots straight into the parent `<tr>`.
 * Keeping these together as one component (instead of three) reads
 * better inline in the parent and the trio always travels as a set.
 */
export function MarketsListVolumeCells({
  volume,
  volume24hr,
  liquidity,
  effectiveSourceFilter,
}: MarketsListVolumeCellsProps) {
  const isKalshi = effectiveSourceFilter === "kalshi";
  const isPM = effectiveSourceFilter === "polymarket";
  const isHL = effectiveSourceFilter === "hyperliquid";
  // Kalshi doesn't expose a 24h breakdown — its "volume" column is
  // already lifetime-equivalent for the visible window, so re-use it.
  const headlineVolume = isKalshi ? volume : volume24hr;
  // Hyperliquid HIP-4 outcomes carry NO volume/OI from HL's public API
  // (only the midpoint via allMids — spotMetaAndAssetCtxs has no outcome
  // rows; outcomeMeta has no vol/OI fields). So both vol columns are always
  // 0/null for HL — render "—" instead of a misleading "$0".
  const fmtVol = (v: number) => (isHL && (!v || v <= 0)) ? "—" : formatCompactUSD(v, 1);

  return (
    <>
      {/* 24h Volume (or just "Volume" for Kalshi) */}
      <td className="px-3 py-2.5 text-right">
        <span className="text-[12px] font-inter-tight font-semibold tabular-nums text-gray-700 dark:text-gray-300">
          {fmtVol(headlineVolume)}
        </span>
      </td>

      {/* Liquidity — PM only. OI was removed across the board, and
          Kalshi/Predict don't ship liquidity numbers, so they drop the
          column entirely (the <colgroup> in the parent already
          accounts for this). */}
      {isPM && (
        <td className="px-3 py-2.5 text-right">
          {liquidity > 0 && (
            <span className="text-[12px] font-inter-tight font-semibold tabular-nums text-gray-700 dark:text-gray-300">
              {formatCompactUSD(liquidity, 0)}
            </span>
          )}
        </td>
      )}

      {/* Total Volume (lifetime) */}
      <td className="px-3 py-2.5 text-right">
        <span className="text-[12px] font-inter-tight font-semibold tabular-nums text-gray-700 dark:text-gray-300">
          {fmtVol(volume)}
        </span>
      </td>
    </>
  );
}
